import type React from 'react';
import { useCallback, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import FormField from '@/components/atoms/FormField';
import Select from '@/components/atoms/Select';
import StyledSingleDatePicker from '@/components/atoms/StyledSingleDatePicker/StyledSingleDatePicker';
import { ModalBody, ModalComponent, ModalFooter } from '@/components/molecules/ModalComponent';
import type { CreateNewContractLicenseInput, LicenseInfo } from '@/domain/licenses';
import { dayStartISOInTimezone, storedDayAsLocalDate } from '@/utils/date';

import styles from './ModalAddLicense.module.scss';

export interface NewLicenseProps {
    onSubmitted: (ev: CreateNewContractLicenseInput) => void;
    licenses: LicenseInfo[];
}

interface FormDataState {
    licenseId?: number;
    dayFrom: Date | null;
    dayTo: Date | null;
}

/** The To day a new licence gets, which reads as "no end". */
const OPEN_ENDED_DAY = '2999-01-01';

function ModalAddLicense({ onSubmitted, licenses }: NewLicenseProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const modalId = useId();
    const licenseSelectId = useId();
    const fromDateId = useId();
    const toDateId = useId();

    const toggle = () => setOpen(!open);

    const [formData, setFormData] = useState<FormDataState>({
        dayTo: storedDayAsLocalDate(OPEN_ENDED_DAY),
        dayFrom: null,
    });
    // The select shows the first licence until one is picked, also when the list arrives
    // after the modal mounted, so the form sends what the select shows.
    const licenseId = formData.licenseId ?? licenses[0]?.id;

    function updateForm<T extends keyof typeof formData>(key: T) {
        return (value: (typeof formData)[T]) =>
            setFormData((formData) => ({ ...formData, [key]: value }));
    }

    const isValid = !!formData.dayFrom && !!licenseId;
    const onFormDataOnSubmit = useCallback(
        (ev: React.FormEvent<HTMLFormElement>) => {
            const { dayFrom, dayTo } = formData;
            if (licenseId && dayFrom) {
                onSubmitted({
                    licenseId,
                    // The days are dates, stored as the calendar day picked in any zone.
                    dayFrom: dayStartISOInTimezone(dayFrom),
                    // A cleared To sends null, as it always has.
                    dayTo: dayTo ? dayStartISOInTimezone(dayTo) : null,
                });
                setOpen(false);
            }
            ev.preventDefault();
        },
        [formData, licenseId, onSubmitted]
    );

    return (
        <>
            <Button
                id={modalId}
                data-testid="openAddLicenseModal"
                onClick={() => setOpen(true)}
                className={styles.openButton}>
                {t('common.actions.addLicense')}
            </Button>
            <ModalComponent
                isOpen={open}
                onClose={toggle}
                centered={false}
                title={t('bioscopeContracts.licenseModal.addTitle')}>
                <form onSubmit={onFormDataOnSubmit}>
                    <ModalBody>
                        <FormField
                            label={t('bioscopeContracts.licenseModal.license')}
                            htmlFor={licenseSelectId}>
                            <Select
                                id={licenseSelectId}
                                name="license"
                                value={licenseId ?? ''}
                                onChange={(ev) =>
                                    updateForm('licenseId')(Number.parseInt(ev.target.value))
                                }>
                                {licenses.map((license) => (
                                    <option key={license.id} value={license.id}>
                                        {license.name}
                                    </option>
                                ))}
                            </Select>
                        </FormField>
                        <FormField label={t('common.labels.from')} htmlFor={fromDateId}>
                            <StyledSingleDatePicker
                                id={fromDateId}
                                required={true}
                                date={formData?.dayFrom ?? null}
                                placeholder={t('common.labels.from')}
                                onDateChange={(date) => updateForm('dayFrom')(date)}
                            />
                        </FormField>
                        <FormField label={t('molecules.common.to')} htmlFor={toDateId}>
                            <StyledSingleDatePicker
                                id={toDateId}
                                date={formData?.dayTo ?? null}
                                placeholder={t('molecules.common.to')}
                                minDate={formData?.dayFrom ?? null}
                                onDateChange={(date) => updateForm('dayTo')(date)}
                            />
                        </FormField>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="secondary" onClick={() => setOpen(false)}>
                            {t('common.actions.cancel')}
                        </Button>
                        <Button variant="primary" type="submit" disabled={!isValid}>
                            {t('molecules.modalAddLicense.add')}
                        </Button>
                    </ModalFooter>
                </form>
            </ModalComponent>
        </>
    );
}

export default ModalAddLicense;
